import React, { useState } from 'react'
import classnames from 'classnames'
import Panel from '../../components/Panel'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { formatConductorDate, formatConductorTimestamp } from './conductorUtils'
import { TD, TH } from './conductorTableCells'
import { useGetConductorSlaReportQuery } from './conductorApi'

const WINDOW_OPTIONS = [7, 30, 90] as const

const StatCard: React.FC<{ label: string, value: React.ReactNode, alert?: boolean }> = ({ label, value, alert }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    <div
      className={classnames(
        'mt-1 text-2xl font-semibold',
        alert === true ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'
      )}
    >
      {value}
    </div>
  </div>
)

export const Reporting: React.FC = () => {
  const [windowDays, setWindowDays] = useState<number>(30)
  const { data, isError, refetch } = useGetConductorSlaReportQuery(windowDays)

  const totalOverdue = data?.overdueWorkItems.reduce((acc, row) => acc + row.count, 0) ?? 0

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">SLA Reporting</h2>
          <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
            Time in stage, SLA timer activity, overdue work and resolution throughput
          </p>
          <ConductorFieldKey screen="reporting" />
          {data != null && (
            <p className="m-0 mt-1 text-xs text-slate-400 dark:text-slate-500">
              As of {formatConductorTimestamp(data.generatedAt)}
            </p>
          )}
        </div>
        <div className="flex gap-1" role="group" aria-label="Trailing window">
          {WINDOW_OPTIONS.map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setWindowDays(days)}
              className={classnames(
                'rounded-md px-3 py-1.5 text-xs font-medium',
                windowDays === days
                  ? 'bg-indigo-600 text-white'
                  : 'border border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
              )}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {isError ? (
        <QueryError message="Failed to load the SLA report." onRetry={() => void refetch()} />
      ) : data == null ? (
        <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="SLA timers scheduled" value={data.timers.scheduled} />
            <StatCard label={`Fired (${data.windowDays}d)`} value={data.timers.firedInWindow} />
            <StatCard
              label={`Escalated (${data.windowDays}d)`}
              value={data.timers.escalatedInWindow}
              alert={data.timers.escalatedInWindow > 0}
            />
            <StatCard label="Overdue work items" value={totalOverdue} alert={totalOverdue > 0} />
          </div>

          <Panel header="Time in current stage (active workflows)">
            {data.timeInStage.length === 0 ? (
              <div className="py-4 text-sm text-slate-400 dark:text-slate-500">No active workflows.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <TH>Playbook</TH>
                    <TH>Stage</TH>
                    <TH align="right">Active</TH>
                    <TH align="right">Avg days</TH>
                    <TH align="right">Max days</TH>
                  </tr>
                </thead>
                <tbody>
                  {data.timeInStage.map((row) => (
                    <tr key={`${row.playbookKey}:${row.stage}`} className="border-t border-slate-100 dark:border-slate-700">
                      <TD className="font-medium">{row.playbookKey}</TD>
                      <TD>{row.stage}</TD>
                      <TD align="right">{row.activeCount}</TD>
                      <TD align="right">{row.avgDaysInStage}</TD>
                      <TD align="right" alert={row.maxDaysInStage > data.windowDays}>
                        {row.maxDaysInStage}
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel header="Overdue work items by role">
              {data.overdueWorkItems.length === 0 ? (
                <div className="py-4 text-sm text-slate-400 dark:text-slate-500">Nothing overdue.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <TH>Role</TH>
                      <TH align="right">Overdue</TH>
                      <TH align="right">Oldest due</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {data.overdueWorkItems.map((row) => (
                      <tr key={row.ownerRole} className="border-t border-slate-100 dark:border-slate-700">
                        <TD className="font-medium">{row.ownerRole}</TD>
                        <TD align="right" alert>
                          {row.count}
                        </TD>
                        <TD align="right">{formatConductorDate(row.oldestDueAt)}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel header={`Resolution throughput (${data.windowDays}d)`}>
              {data.resolutionTimes.length === 0 ? (
                <div className="py-4 text-sm text-slate-400 dark:text-slate-500">No resolutions in this window.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <TH>Role</TH>
                      <TH align="right">Resolved</TH>
                      <TH align="right">Avg days to resolve</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {data.resolutionTimes.map((row) => (
                      <tr key={row.ownerRole} className="border-t border-slate-100 dark:border-slate-700">
                        <TD className="font-medium">{row.ownerRole}</TD>
                        <TD align="right">{row.resolvedCount}</TD>
                        <TD align="right">{row.avgDaysToResolve}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  )
}

export default Reporting
